<template>
  <div class="tags-container">
    <div
      ref="tags"
      class="tags"
      @scroll.passive="handleContainerScroll"
    >
      <div
        v-for="(lineTags, idx) in tagLines"
        :key="idx"
        class="line"
      >
        <Tag
          v-for="tag in lineTags"
          :key="tag.id"
          class="tag"
          :tag="tag"
          @click-tag="onClickTag(tag)"
        />
      </div>
    </div>

    <SlideButton
      type="left"
      class="slide-left"
      :class="{ visible: leftScrollVisible }"
      @click="
        scrollLeft();
        sendAnalytics();
      "
    />

    <SlideButton
      type="right"
      class="slide-right"
      :class="{ visible: rightScrollVisible }"
      @click="
        scrollRight();
        sendAnalytics();
      "
    />
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import type { ITag } from './Tag.vue';
  import Tag from './Tag.vue';
  import SlideButton from './SlideButton.vue';
  import { getFullUrl } from '~/utils';

  export default defineNuxtComponent({
    name: 'Tags',
    components: { Tag, SlideButton },

    props: {
      tags: {
        type: Array as PropType<ITag[]>,
        required: true,
      },

      rows: {
        type: Number,
        default: 2,
      },
    },

    data: () => ({
      leftScrollVisible: false,
      rightScrollVisible: false,
      isFirstScroll: true,
      timerFinished: false,
    }),

    computed: {
      tagLines(): ITag[][] {
        // сложная логика нужна чтобы распределять равномерно элементы по строкам

        const basePart = Math.floor(this.tags.length / this.rows);
        const remainder = this.tags.length % this.rows;
        const result = [];

        for (let start = 0, i = 0; i < this.rows; i++) {
          // If there's still remainder, add 1 to the current row's length
          const end = start + basePart + (i < remainder ? 1 : 0);
          result.push(this.tags.slice(start, end));
          start = end;
        }

        return result;
      },
    },

    async mounted() {
      // почему-то без этого scrollWidth не определяется корректно
      await this.$nextTick();
      // показать кнопки для скролла
      this.handleContainerScroll();
      setTimeout(() => {
        this.timerFinished = true;
      }, 3000);
    },

    methods: {
      handleContainerScroll(): void {
        const container = this.$refs.tags as HTMLDivElement;
        this.leftScrollVisible = container.scrollLeft > 0;
        // -2 поправка на неточность скролла мышкой
        this.rightScrollVisible =
          container.scrollLeft < container.scrollWidth - container.offsetWidth - 2;
        this.syncLinesOffset();

        // * при загрузке страницы, есть микроскролл из-за высчитывания, поэтому ложно срабатывает аналитика
        if (this.isFirstScroll && this.timerFinished) {
          this.isFirstScroll = false;
          this.$sendYandexMetrika({
            level1: 'Скролл_Теги',
            level6: '1',
          });
        }
      },

      onClickTag(tag: Tag) {
        this.$sendYandexMetrika({
          level1: 'Клик_Теги',
          level4: getFullUrl(tag.url),
          level6: tag.name,
        });
      },

      syncLinesOffset(): void {
        // Нужно чтобы в конце все бэйджи заканчивались на одной вертикали
        const container = this.$refs.tags as HTMLDivElement;
        const lines = Array.from(container.children) as HTMLDivElement[];
        const max = lines.reduce(
          (acc, line) => (line.scrollWidth > acc ? line.scrollWidth : acc),
          0,
        );
        const totalScrollWidth = container.scrollWidth - container.offsetWidth;
        const scrollPercent = container.scrollLeft / totalScrollWidth;
        lines.forEach((line) => {
          const offsetSize = (max - line.scrollWidth) * scrollPercent;
          line.style.transform = `translateX(${offsetSize}px)`;
        });
      },

      scrollLeft(): void {
        // Делим оставшийся размер скролла на части, чтобы избежать маленького скролла
        const container = this.$refs.tags as HTMLDivElement;
        const maxScroll = container.offsetWidth * 0.9;
        const distanceLeft = container.scrollLeft;
        const scrollCount = Math.ceil(distanceLeft / maxScroll);
        const distance = distanceLeft / scrollCount;

        container.scrollLeft -= distance;
      },

      sendAnalytics() {
        if (this.isFirstScroll) {
          this.isFirstScroll = false;
          this.$sendYandexMetrika({
            level1: 'Скролл_Теги',
            level6: '1',
          });
        }
      },

      scrollRight(): void {
        // Делим оставшийся размер скролла на части, чтобы избежать маленького скролла
        const container = this.$refs.tags as HTMLDivElement;
        const maxScroll = container.offsetWidth * 0.9;
        const distanceLeft = container.scrollWidth - container.scrollLeft;
        const scrollCount = Math.ceil(distanceLeft / maxScroll);
        const distance = distanceLeft / scrollCount;

        container.scrollLeft += distance;
      },
    },
  });
</script>

<style lang="scss" scoped>
  .tags-container {
    position: relative;
    overflow: hidden;
  }

  .tags {
    overflow-x: auto;
    padding: 10px;
    scrollbar-width: none;
    scroll-behavior: smooth;

    &:hover {
      will-change: scroll-position;
    }

    @include tablet {
      padding: 18px;
    }
  }

  .slide-left,
  .slide-right {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    transition:
      0.3s opacity ease-in-out,
      0.3s left ease-in-out,
      0.3s right ease-in-out;
    opacity: 0;
    margin-top: 0;
    margin-bottom: 0;

    &.visible {
      opacity: 1;
    }

    @media (hover: none) {
      display: none;
    }
  }
  .slide-left {
    left: -8px;

    &.visible {
      left: 16px;
    }
  }
  .slide-right {
    right: -8px;

    &.visible {
      right: 16px;
    }
  }

  .line {
    display: flex;
    max-width: 0;

    .tags:hover & {
      will-change: transform;
    }
  }
  .tag {
    flex-shrink: 0;

    &:last-child {
      padding-right: 24px;
    }
  }
</style>
